import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput, SelectInput, FieldRow } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { getHeroPlanningEmployees } from "@/lib/projects";
import { normalizeEmployeeLogin } from "@/lib/auth";
import {
  createRow,
  updateRow,
  deleteRow,
  reorderRows,
  PLANUNG_COLORS,
} from "@/lib/montageplanung";
import type { PlanungRow } from "@/lib/montageplanung";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";
import "./montageplanung.css";

interface HeroEmp {
  login: string;
  name: string;
}

const ROW_DRAG_TYPE = "application/x-montageplanung-row";

export interface RowManagerDialogProps {
  open: boolean;
  onClose: () => void;
  rows: PlanungRow[];
  onChanged: () => void;
}

interface SortableRowItemProps {
  row: PlanungRow;
  index: number;
  busy: boolean;
  dragIndex: number | null;
  dropIndex: number | null;
  dropBefore: boolean;
  onDragStart: (index: number) => void;
  onDragEnd: () => void;
  onDragOver: (index: number, before: boolean) => void;
  onDragLeave: () => void;
  onDrop: (index: number, before: boolean) => void;
  onUpdateLabel: (id: number, label: string) => void;
  onUpdateColor: (id: number, color: string) => void;
  onDelete: (id: number) => void;
}

function SortableRowItem({
  row,
  index,
  busy,
  dragIndex,
  dropIndex,
  dropBefore,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onUpdateLabel,
  onUpdateColor,
  onDelete,
}: SortableRowItemProps) {
  const isDragging = dragIndex === index;
  const isDropTarget = dropIndex === index;

  return (
    <div
      className={clsx(
        "mp-row-item",
        row.kind === "separator" && "is-separator",
        isDragging && "is-dragging",
        isDropTarget && dropBefore && "is-drop-before",
        isDropTarget && !dropBefore && "is-drop-after",
      )}
      draggable={!busy}
      onDragStart={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("input, select, button")) {
          e.preventDefault();
          return;
        }
        onDragStart(index);
        e.dataTransfer.setData(ROW_DRAG_TYPE, String(row.id));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        if (dragIndex === null || dragIndex === index) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        onDragOver(index, e.clientY - rect.top < rect.height / 2);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        if (dragIndex === null) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        onDrop(index, e.clientY - rect.top < rect.height / 2);
      }}
    >
      <span
        className="mp-row-handle"
        title="Ziehen zum Sortieren"
        aria-label="Zeile verschieben"
        role="img"
      >
        ⠿
      </span>
      <span
        className={clsx(
          "h-3 w-3 shrink-0 rounded-full border border-separator-opaque",
          row.kind === "separator" && "border-dashed bg-transparent",
        )}
        style={{ backgroundColor: row.kind === "separator" ? undefined : row.color || "transparent" }}
      />
      <div className="min-w-0 flex-1">
        {row.kind === "separator" ? (
          <input
            className="eh-input h-8"
            defaultValue={row.label || ""}
            placeholder="Optional: Beschriftung (z. B. Montage)"
            disabled={busy}
            onMouseDown={(e) => e.stopPropagation()}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val !== (row.label || "")) {
                onUpdateLabel(row.id, val);
              }
            }}
          />
        ) : row.system_key === "meros" ? (
          <span className="text-sm">Meros</span>
        ) : row.kind === "resource" ? (
          <input
            className="eh-input h-8"
            defaultValue={row.label || ""}
            disabled={busy}
            onMouseDown={(e) => e.stopPropagation()}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && val !== (row.label || "")) {
                onUpdateLabel(row.id, val);
              }
            }}
          />
        ) : (
          <span className="line-clamp-2 break-words text-sm">
            {row.label || row.employee_login}
            <span className="ml-1 text-xs text-label-secondary">Mitarbeiter</span>
          </span>
        )}
      </div>
      {row.kind === "separator" && (
        <span className="shrink-0 text-xs text-label-secondary">Trennlinie</span>
      )}
      {row.system_key === "meros" && (
        <span className="shrink-0 text-xs text-label-secondary">System</span>
      )}
      {row.kind === "resource" && row.system_key !== "meros" && (
        <select
          className="eh-input h-8 w-24"
          value={row.color || ""}
          disabled={busy}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => onUpdateColor(row.id, e.target.value)}
        >
          <option value="">Farbe…</option>
          {PLANUNG_COLORS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      )}
      {row.system_key === "meros" ? null : (
      <button
        type="button"
        className="px-1 text-status-red hover:underline disabled:opacity-40"
        disabled={busy}
        onClick={() => {
          const msg =
            row.kind === "separator"
              ? "Trennlinie entfernen?"
              : "Zeile inkl. ihrer Einsätze entfernen?";
          if (window.confirm(msg)) {
            onDelete(row.id);
          }
        }}
        title="Entfernen"
      >
        ✕
      </button>
      )}
    </div>
  );
}

export function RowManagerDialog({ open, onClose, rows, onChanged }: RowManagerDialogProps) {
  const [employees, setEmployees] = useState<HeroEmp[]>([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [resourceLabel, setResourceLabel] = useState("");
  const [resourceColor, setResourceColor] = useState(PLANUNG_COLORS[7]!.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [dropBefore, setDropBefore] = useState(true);

  useEffect(() => {
    if (!open) return;
    getHeroPlanningEmployees()
      .then((list) => {
        const mapped = list
          .map((p) => ({
            login: normalizeEmployeeLogin(String(p.email || "")),
            name: String(p.name || p.email || "").trim(),
          }))
          .filter((e) => e.login)
          .sort((a, b) => a.name.localeCompare(b.name, "de"));
        setEmployees(mapped);
      })
      .catch(() => setEmployees([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDragIndex(null);
      setDropIndex(null);
    }
  }, [open]);

  const existingLogins = useMemo(
    () => new Set(rows.filter((r) => r.employee_login).map((r) => r.employee_login)),
    [rows],
  );

  const availableEmployees = useMemo(
    () => employees.filter((e) => !existingLogins.has(e.login)),
    [employees, existingLogins],
  );

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function handleAddEmployee() {
    const emp = employees.find((e) => e.login === selectedEmp);
    if (!emp) return;
    await run(async () => {
      await createRow({ kind: "employee", employee_login: emp.login, label: emp.name });
      setSelectedEmp("");
    });
  }

  async function handleAddResource() {
    const label = resourceLabel.trim();
    if (!label) return;
    await run(async () => {
      await createRow({ kind: "resource", label, color: resourceColor });
      setResourceLabel("");
    });
  }

  async function handleAddSeparator() {
    await run(() => createRow({ kind: "separator" }));
  }

  function clearDragState() {
    setDragIndex(null);
    setDropIndex(null);
  }

  async function handleDrop(targetIndex: number, before: boolean) {
    if (dragIndex === null) return;
    const from = dragIndex;
    let to = before ? targetIndex : targetIndex + 1;
    if (from < to) to -= 1;
    clearDragState();
    if (from === to) return;

    const ids = rows.map((r) => r.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved!);
    await run(() => reorderRows(ids));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={selectedEmp !== "" || resourceLabel.trim() !== ""}
      title="Zeilen verwalten"
      description="Mitarbeiter, Ressourcen und Trennlinien per Drag-and-Drop sortieren, hinzufügen oder entfernen."
      size="lg"
      fullScreenMobile
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-5">
        {error && <Alert variant="error">{error}</Alert>}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-label">Aktuelle Zeilen ({rows.length})</h3>
          <div className="divide-y divide-separator rounded-lg border border-separator-opaque overflow-hidden">
            {rows.length === 0 && (
              <div className="px-3 py-3 text-sm text-label-secondary">Noch keine Zeilen angelegt.</div>
            )}
            {rows.map((row, index) => (
              <SortableRowItem
                key={row.id}
                row={row}
                index={index}
                busy={busy}
                dragIndex={dragIndex}
                dropIndex={dropIndex}
                dropBefore={dropBefore}
                onDragStart={setDragIndex}
                onDragEnd={clearDragState}
                onDragOver={(idx, before) => {
                  setDropIndex(idx);
                  setDropBefore(before);
                }}
                onDragLeave={() => {
                  setDropIndex(null);
                }}
                onDrop={(idx, before) => void handleDrop(idx, before)}
                onUpdateLabel={(id, label) => void run(() => updateRow(id, { label }))}
                onUpdateColor={(id, color) => void run(() => updateRow(id, { color }))}
                onDelete={(id) => void run(() => deleteRow(id))}
              />
            ))}
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2 rounded-lg border border-separator-opaque p-3">
            <h3 className="text-sm font-semibold text-label">Mitarbeiter hinzufügen</h3>
            <SelectInput
              value={selectedEmp}
              onChange={(e) => setSelectedEmp(e.target.value)}
            >
              <option value="">Mitarbeiter wählen…</option>
              {availableEmployees.map((e) => (
                <option key={e.login} value={e.login}>
                  {e.name}
                </option>
              ))}
            </SelectInput>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || !selectedEmp}
              onClick={() => void handleAddEmployee()}
            >
              Hinzufügen
            </Button>
          </div>

          <div className="space-y-2 rounded-lg border border-separator-opaque p-3">
            <h3 className="text-sm font-semibold text-label">Ressource hinzufügen</h3>
            <TextInput
              value={resourceLabel}
              onChange={(e) => setResourceLabel(e.target.value)}
              placeholder="z. B. LKW, Opel Movano, ZINQ"
            />
            <FieldRow label="Farbe">
              <select
                className="eh-input"
                value={resourceColor}
                onChange={(e) => setResourceColor(e.target.value)}
              >
                {PLANUNG_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </FieldRow>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || !resourceLabel.trim()}
              onClick={() => void handleAddResource()}
            >
              Hinzufügen
            </Button>
          </div>

          <div className="space-y-2 rounded-lg border border-separator-opaque p-3">
            <h3 className="text-sm font-semibold text-label">Trennlinie hinzufügen</h3>
            <p className="text-xs text-label-secondary">
              Optische Gruppierung zwischen Mitarbeitern oder Ressourcen. Optional mit Beschriftung
              in der Liste bearbeiten.
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void handleAddSeparator()}
            >
              Trennlinie einfügen
            </Button>
          </div>
        </section>
      </div>
    </Dialog>
  );
}
